/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Stack } from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CustomTooltip, LegendToggleButton } from '@opengeoweb/shared';
import {
  mapSelectors,
  loadingIndicatorActions,
  genericSelectors,
  useUpdateFeatureSyncLayers,
  mapActions,
  useRegisterFeatureSyncSource,
  useSyncgroupTimeState,
} from '@opengeoweb/store';
import { LockLocked, LockUnlocked } from '@opengeoweb/theme';
import { webmapUtils } from '@opengeoweb/webmap';
import {
  TIME_SERIES_CONTAINER_BREAKPOINT_PX,
  TIME_SERIES_CONTAINER_NAME,
} from './constants';
import { LocationInfoButtonConnect } from './LocationInfo/LocationInfoButtonConnect';
import { LocationSelectButtonConnect } from './LocationSelect/LocationSelectButtonConnect';
import { TimeSeriesMarkersSwitch } from './TimeSeriesMarkersSwitch';
import { TimeSeriesTopRowButton } from './TimeSeriesTopRowButton';
import { TimeSeriesView } from './TimeSeriesView';
import type { PlotPreset, TimeSeriesLocation } from './types';
import { useTimeSeriesLocations } from './useTimeSeriesLocations';
import { consolidateFeatures } from './utils';
import { timeSeriesActions } from '../../store';
import {
  getCurrentLocation,
  getIsLegendVisible,
  getIsMapPinDisabled,
  getPlotState,
  getServices,
  getShouldHideLocationMarkers,
} from '../../store/selectors';
import type { TimeSeriesRootState } from '../../store/types';
import { useTimeseriesTranslation } from '../../utils/i18n';
import TimeSeriesManagerMapButtonConnect from '../TimeSeriesManager/TimeSeriesManagerMapButtonConnect';

const useMapPin = ({
  panelId,
}: {
  panelId: string;
}): {
  toggleDisableMapPin: () => void;
  isMapPinDisabled: boolean;
} => {
  const dispatch = useDispatch();

  const selectedMapIds = useSelector((state: TimeSeriesRootState) =>
    genericSelectors.getLinkedTargetIds(state, panelId),
  );

  const isMapPinDisabled = useSelector((state: TimeSeriesRootState) =>
    getIsMapPinDisabled(state, panelId),
  );

  /* Make sure the mappin is visible when linking new maps */
  useEffect(() => {
    const toggleMapPinVisibility = (displayMapPin: boolean): void => {
      selectedMapIds.forEach((mapId) => {
        dispatch(
          mapActions.toggleMapPinIsVisible({
            mapId,
            displayMapPin,
          }),
        );
      });
    };

    toggleMapPinVisibility(true);

    return (): void => {
      // Hide the map pin when the component is unmounted
      toggleMapPinVisibility(false);
    };
  }, [dispatch, selectedMapIds]);

  const toggleDisableMapPin = (): void => {
    const newDisableMapPinState = !isMapPinDisabled;

    dispatch(
      timeSeriesActions.disableMapPin({
        panelId,
        isMapPinDisabled: newDisableMapPinState,
      }),
    );

    selectedMapIds.forEach((mapId) => {
      dispatch(
        mapActions.setDisableMapPin({
          mapId,
          disableMapPin: newDisableMapPinState,
        }),
      );
    });
  };

  return { toggleDisableMapPin, isMapPinDisabled };
};

export interface TimeSeriesConnectProps {
  panelId: string;
}

export const TimeSeriesConnect: React.FC<TimeSeriesConnectProps> = ({
  panelId,
}) => {
  const dispatch = useDispatch();
  const { t } = useTimeseriesTranslation();
  const geojsonLayerId = React.useRef(webmapUtils.generateLayerId()).current;

  const shouldShowLegend = useSelector((state: TimeSeriesRootState) =>
    getIsLegendVisible(state, panelId),
  );
  const services = useSelector(
    (state: TimeSeriesRootState) => getServices(state) || [],
  );
  const shouldHideLocationMarkers = useSelector((state: TimeSeriesRootState) =>
    getShouldHideLocationMarkers(state, panelId),
  );
  const plotPresetState: PlotPreset | undefined = useSelector(
    (state: TimeSeriesRootState) => getPlotState(state, panelId),
  );
  const linkedMapIds = useSelector((state: TimeSeriesRootState) =>
    genericSelectors.getLinkedTargetIds(state, panelId),
  );
  const panelMapId = linkedMapIds?.[0] ?? plotPresetState?.mapId;
  const timeSeriesLocation = useSelector((state: TimeSeriesRootState) =>
    getCurrentLocation(state, panelId),
  );
  const selectedFeatureFromPanel = useSelector((state: TimeSeriesRootState) =>
    genericSelectors.getSelectedFeature(state, panelId),
  );
  const selectedFeatureFromMap = useSelector((state: TimeSeriesRootState) =>
    panelMapId ? genericSelectors.getSelectedFeature(state, panelMapId) : null,
  );
  const selectedFeature = selectedFeatureFromPanel ?? selectedFeatureFromMap;
  const allMaps = useSelector((store: TimeSeriesRootState) =>
    mapSelectors.getAllMapsByIds(store),
  );

  const { toggleDisableMapPin, isMapPinDisabled } = useMapPin({
    panelId,
  });

  const isLoading = useCallback(
    (startOrFinish: boolean): void => {
      dispatch(
        loadingIndicatorActions.setEDRIsLoading({
          id: panelId,
          isEDRLoading: startOrFinish,
        }),
      );
    },
    [dispatch, panelId],
  );

  const setTimeSeriesLocation = useCallback(
    (location: TimeSeriesLocation | undefined): void => {
      dispatch(timeSeriesActions.setCurrentLocation({ panelId, location }));
    },
    [dispatch, panelId],
  );

  const memoizedSelectedFeature: TimeSeriesLocation | null =
    React.useMemo(() => {
      if (!selectedFeature) {
        return null;
      }

      return {
        lat: selectedFeature.lat,
        lon: selectedFeature.lon,
        id: selectedFeature.id,
        occurrences: selectedFeature.occurrences,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFeature?.lat, selectedFeature?.lon]);

  useEffect(() => {
    if (!memoizedSelectedFeature && panelMapId) {
      const activeMapCoordinates = allMaps[panelMapId]?.mapPinLocation;

      if (
        activeMapCoordinates?.lat !== undefined &&
        activeMapCoordinates?.lon !== undefined
      ) {
        setTimeSeriesLocation(activeMapCoordinates);
      }
      return;
    }

    if (memoizedSelectedFeature) {
      const validLocation: TimeSeriesLocation = {
        lat: memoizedSelectedFeature.lat,
        lon: memoizedSelectedFeature.lon,
        id: memoizedSelectedFeature.id,
        occurrences: memoizedSelectedFeature.occurrences,
      };

      setTimeSeriesLocation(validLocation);
    }
  }, [
    panelMapId,
    selectedFeature,
    setTimeSeriesLocation,
    memoizedSelectedFeature,
    allMaps,
  ]);

  useRegisterFeatureSyncSource(panelId);

  const {
    combinedGeoJson,
    isLocationsFetching,
    serviceListForLocations,
    splitGeoJsonByCollection,
  } = useTimeSeriesLocations(panelId);

  const featureSyncLayersById = useMemo(
    () =>
      serviceListForLocations.length
        ? {
            [geojsonLayerId]: {
              id: geojsonLayerId,
              geojson: consolidateFeatures([
                {
                  id: 'combined-layer',
                  geoJSON: combinedGeoJson,
                },
              ]),
            },
          }
        : {},
    [combinedGeoJson, geojsonLayerId, serviceListForLocations.length],
  );

  useUpdateFeatureSyncLayers(featureSyncLayersById, panelId);

  /* Set the loading state based on the locations fetching status */
  useEffect(() => {
    isLoading(isLocationsFetching);
  }, [isLoading, isLocationsFetching]);

  /* Create GeoJSON arrays and draw the geojson layer based on the locationsData */
  useEffect(() => {
    if (serviceListForLocations.length) {
      if (combinedGeoJson.features.length === 0) {
        setTimeSeriesLocation(undefined);
      }
    }
  }, [
    combinedGeoJson.features.length,
    serviceListForLocations.length,
    setTimeSeriesLocation,
  ]);

  const toggleLocationMarkers = (shouldHide: boolean): void => {
    dispatch(
      timeSeriesActions.toggleLocationMarkers({
        panelId,
        shouldHideLocationMarkers: shouldHide,
      }),
    );
  };

  const toggleLegend = (): void => {
    dispatch(
      timeSeriesActions.toggleLegend({
        panelId,
        isLegendVisible: !shouldShowLegend,
      }),
    );
  };

  const onToggleLegendParam = (parameterId: string): void => {
    dispatch(
      timeSeriesActions.toggleParameter({
        parameterId,
        panelId,
      }),
    );
  };

  const syncgroupTimeState = useSyncgroupTimeState(panelId, 1000);
  return (
    <Stack
      data-testid="TimeSeriesConnect"
      sx={{
        padding: '20px',
        width: '100%',
        height: '100%',
        backgroundColor: 'geowebColors.background.surfaceApp',
        containerType: 'inline-size',
        containerName: TIME_SERIES_CONTAINER_NAME,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          maxWidth: '750px',
          marginBottom: '4px',
          marginLeft: '10px',
        }}
      >
        <TimeSeriesManagerMapButtonConnect viewId={panelId} />
        {!!serviceListForLocations.length && (
          <LocationSelectButtonConnect panelId={panelId} />
        )}
        <LocationInfoButtonConnect panelId={panelId} t={t} />

        <CustomTooltip
          title={
            isMapPinDisabled
              ? t('timeseries-unlock-location')
              : t('timeseries-lock-location')
          }
        >
          <span>
            <TimeSeriesTopRowButton
              data-testid="toggleLockLocationButton"
              icon={isMapPinDisabled ? <LockLocked /> : <LockUnlocked />}
              longText={
                isMapPinDisabled
                  ? t('timeseries-unlock-location')
                  : t('timeseries-lock-location')
              }
              onClick={(): void => toggleDisableMapPin()}
              selected={isMapPinDisabled}
            />
          </span>
        </CustomTooltip>
        <TimeSeriesMarkersSwitch
          shouldHideMarkers={shouldHideLocationMarkers}
          toggleMarkers={toggleLocationMarkers}
        />
        <LegendToggleButton
          hideTooltipText={t('timeseries-legend-tooltip-hide')}
          isLegendVisible={shouldShowLegend}
          legendLabel={t('timeseries-legend')}
          onToggle={toggleLegend}
          showTooltipText={t('timeseries-legend-tooltip-show')}
          sx={{
            [`@container ${TIME_SERIES_CONTAINER_NAME} (width < ${TIME_SERIES_CONTAINER_BREAKPOINT_PX})`]:
              {
                maxWidth: '90px',
                '& .longText': { display: 'none' },
                '& svg': { marginRight: '0 !important' },
              },
            [`@container ${TIME_SERIES_CONTAINER_NAME} (min-width: ${TIME_SERIES_CONTAINER_BREAKPOINT_PX})`]:
              {
                width: '130px',
              },
          }}
        />
      </Stack>
      {!!timeSeriesLocation && !!plotPresetState && (
        <Box sx={{ overflow: 'auto' }}>
          {!!plotPresetState && (
            <TimeSeriesView
              isLoading={isLoading}
              onToggleLegendParam={onToggleLegendParam}
              plotPreset={plotPresetState}
              selectedLocation={timeSeriesLocation}
              services={services}
              shouldShowLegend={shouldShowLegend}
              splitGeoJsonByCollection={splitGeoJsonByCollection}
              syncgroupTimeState={syncgroupTimeState}
            />
          )}
        </Box>
      )}
    </Stack>
  );
};
